import styled from '@emotion/styled';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { INewsStory } from '../../shared/api/contentful/types/INewsStory';
import { PageHeader } from '../../shared/components';
import { PageLayout } from '../../shared/layout/page/PageLayout';
import { INewsStoriesPageData } from '../domain/models/INewsStoriesPageData';
import { useSearch } from '../domain/hooks/useSearch';
import { PageNavigation } from './components/PageNavigation';
import { SearchBar } from './components/SearchBar';
import { usePagination } from '../domain/hooks/usePagination';
import { NewsStoriesList } from './components/news-stories/NewsStoriesList';
import {
	YearFilterValue,
	useAllYearsFilters,
} from '../domain/hooks/useAllYearsFilters';
import Responsive from '../../../ui/utils/Responsive';
import ContentWrap from '../../../ui/components/Layout/ContentWrap';
import { getSessionStorageItem } from '../../shared/utils/sessionStorage';
import { useTranslation } from '../../shared/i18n/translate/useTranslation';
import { useNavigate } from '../../shared/router/useNavigate';
import { getLocalizedPath } from '../../app/pages/ui/utils/getLocalizedPath';
import { useLocale } from '../../shared/i18n/locale/useLocale';
import { ALL_CATEGORIES } from '../../shared/domain/utils/getContentCategories';
import { VisuallyHidden } from '../../../ui/components/VisuallyHidden/VisuallyHidden';

export const NewsStoriesPage = ({
	page,
	newsStories,
	pageNumber,
	itemsPerPage,
	categoriesFilter,
}: INewsStoriesPageData) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const locale = useLocale();

	const yearsFilter: YearFilterValue[] = useAllYearsFilters(newsStories);

	const categoriesFiltersWithAllCategories = useMemo(
		() => [
			{ id: ALL_CATEGORIES, title: t('news_searchbar_all_categories') },
			...categoriesFilter,
		],
		[categoriesFilter],
	);

	// Use default values for initial render to avoid hydration mismatch
	const defaultYear = yearsFilter[0];
	const defaultCategory = categoriesFiltersWithAllCategories[0];

	const { searchParams, setSearchParams, filterList } = useSearch<INewsStory>(
		defaultYear,
		defaultCategory,
	);

	// Track whether we've initialized from sessionStorage
	const [hasInitialized, setHasInitialized] = useState(false);

	// Read stored filters from sessionStorage after hydration
	useEffect(() => {
		if (hasInitialized) return;

		const storedYear = getSessionStorageItem('news-stories-year-filter');
		const storedCategoryId = getSessionStorageItem(
			'news-stories-category-filter',
		);

		const sessionYear = storedYear || defaultYear;
		const sessionCategory =
			categoriesFiltersWithAllCategories.find(
				(category) => category.id === storedCategoryId,
			) || defaultCategory;

		if (storedYear || storedCategoryId) {
			setSearchParams({
				year: sessionYear,
				category: sessionCategory,
				query: '',
			});
		}

		setHasInitialized(true);
	}, [
		hasInitialized,
		categoriesFiltersWithAllCategories,
		defaultYear,
		defaultCategory,
		setSearchParams,
	]);

	const filteredNewsStories: INewsStory[] = useMemo(
		() => filterList(newsStories),
		[searchParams, newsStories],
	);

	const { currentPageItems, numberOfPages } = usePagination({
		list: filteredNewsStories,
		pageNumber,
		itemsPerPage,
	});

	const newsListRef = useRef<HTMLElement>(null);
	const isPaginatingRef = useRef(false);

	useEffect(() => {
		if (isPaginatingRef.current) {
			isPaginatingRef.current = false;
			newsListRef.current?.focus();
		}
	}, [pageNumber]);

	return (
		<PageLayout showAlertsDialog page={page}>
			<StyledPageHeader page={page} />
			<SearchBar
				categories={categoriesFiltersWithAllCategories}
				years={yearsFilter}
				onSearch={async (value) => {
					await navigate(getLocalizedPath(`/news/stories/1`, locale));

					setSearchParams(value);
				}}
			/>
			<VisuallyHidden as="div" aria-live="polite" role="status">
				{t('poi_filter_results_count', {
					total: filteredNewsStories.length,
				})}
			</VisuallyHidden>
			<StyledContentWrap>
				<section
					ref={newsListRef}
					tabIndex={-1}
					aria-label={t('pagination_label', { pageNumber, numberOfPages })}
				>
					<NewsStoriesList newsStories={currentPageItems} />
				</section>
				{numberOfPages > 1 && (
					<PageNavigation
						pageNumber={pageNumber}
						numberOfPages={numberOfPages}
						onPageChange={() => {
							isPaginatingRef.current = true;
						}}
					/>
				)}
			</StyledContentWrap>
		</PageLayout>
	);
};

export const StyledPageHeader = styled(PageHeader)`
	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		min-height: 200px;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding: 16px;
		display: flex;
		align-items: center;
		margin-bottom: 100px;

		${ContentWrap} {
			margin: 0;
		}
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
		margin-bottom: 50px;
	}
`;

const StyledContentWrap = styled(ContentWrap)`
	display: flex;
	flex-direction: column;
	gap: 32px;
`;
